import React, { Children, useEffect } from 'react';
import { ButtonType } from '../../../types/ButtonType';
import { CollapsibleContentPortal } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay';
import useCollapsibleOverlay from '../../../hooks/useCollapsibleOverlay';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Icons } from '../../../components/atoms/SVG';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { CollapsibleModifier } from '../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';
import { appendModifiers, Modifier } from '../../../lib/bemUtils/Modifier';

type ContextMenuProps = {
  tooltipText: string;
  children: any;
  tooltipPlacement?: TooltipPlacement;
  modifier?: Modifier<CollapsibleModifier.CONTEXT_MENU_CENTERED>;
};

export function ContextMenu({
  tooltipText,
  children,
  modifier,
  tooltipPlacement = TooltipPlacement.RIGHT,
}: ContextMenuProps) {
  const { collapsibleOpen, collapsibleProps, toggleProps, popperInstance } = useCollapsibleOverlay(
    modifier === CollapsibleModifier.CONTEXT_MENU_CENTERED ? 'bottom' : 'bottom-end',
  );

  /**
   * Trigger update function of popper manually.
   * Context menu often changes position in DOM
   *  -> popper cant know when this happens if children are rendered at the end of the DOM.
   *  -> https://popper.js.org/docs/v2/modifiers/event-listeners/
   *
   * No Deps array as we want to recalculate everytime the context menu renders,
   * but we restrict the update only on open popper instances
   */
  useEffect(() => {
    if (collapsibleOpen) {
      popperInstance?.update();
    }
  });

  return (
    <div className="context-menu">
      <Btn
        reference={toggleProps.ref}
        onClick={toggleProps.onClick}
        onKeyDown={toggleProps.onKeyDown}
        tooltipPlacement={tooltipPlacement}
        type={ButtonType.BUTTON}
        svgSpriteId={collapsibleOpen ? Icons.DECLINE_CROSS : Icons.MORE_MENU}
        text={tooltipText}
        titleText={tooltipText}
        hiddenText
        ariaHaspopup
        ariaControls={toggleProps['aria-controls']}
        ariaExpanded={toggleProps['aria-expanded']}
        modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
      />
      <CollapsibleContentPortal
        {...collapsibleProps}
        modifier={appendModifiers(modifier, CollapsibleModifier.CONTEXT_MENU)}
      >
        {/* ul is never empty because ContextMenu always has children */}
        <ul className="context-menu__button-list">
          {Children.map(children, child => (
            <li>{child}</li>
          ))}
        </ul>
      </CollapsibleContentPortal>
    </div>
  );
}
